import { ChangeEvent, memo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import MailOutlineIcon from "@mui/icons-material/MailOutline";

import { isValidEmail } from "utility/is_valid_email";

// -----------------------------------------------------------------------------

export const LoginForm = memo(function LoginForm({
  login,
}: Props): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation() as { state: locationState };
  const justRegisteredEmail = location.state?.justRegisteredEmail;

  //
  const [emailAddress, setEmailAddress] = useState(justRegisteredEmail ?? "");

  //
  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    login(emailAddress);
  }

  //
  const canLogin = isValidEmail(emailAddress);

  //
  return (
    <Box sx={{ textAlign: "center" }}>
      <Typography variant="h5" fontWeight="bold">
        Sign In
      </Typography>

      <Typography variant="body1">
        You&apos;ll be redirected automatically if your ThreatKey organization
        enabled Single Sign-On (SSO).
      </Typography>

      <form onSubmit={onSubmit}>
        <Box sx={{ mt: 2.5 }}>
          <TextField
            fullWidth
            label="Email Address"
            variant="outlined"
            value={emailAddress}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setEmailAddress(e.target.value)
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <LoadingButton
            fullWidth
            variant="contained"
            type="submit"
            disabled={!canLogin}
          >
            Log in
          </LoadingButton>
        </Box>
      </form>

      <Box sx={{ mt: 3 }}>
        <Typography variant="body1">
          Don&apos;t have an account?{" "}
          <Button
            size="small"
            onClick={() => navigate("/register")}
            sx={{ fontWeight: "bold" }}
          >
            REGISTER
          </Button>
        </Typography>
        <Typography variant="body2">
          By logging in, you agree to our{" "}
          <a href="https://www.threatkey.com/policies/privacy-policy">
            Privacy Policy
          </a>
          .
        </Typography>
      </Box>
    </Box>
  );
});

// -----------------------------------------------------------------------------

interface Props {
  login: Function;
}

interface locationState {
  justRegisteredEmail?: string;
}
